import React, { useState, useRef, useEffect } from 'react';
import { Typography, Box, Paper, IconButton } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const OrdersIdsDropDown = ({ title, count, orders = [], maxHeight = 300 }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  // Handle desktop hover functionality
  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsHovering(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 300);
  };

  // Toggle dropdown for touch devices
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Show dropdown if either hovering (desktop) or opened (touch)
  const showDropdown = (isHovering || isOpen) && orders.length > 0;

  return (
    <Box 
      sx={{ position: 'relative' }}
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600, color: '#333', mb: 1 }}>
            {title}
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontSize: '80px', 
              fontWeight: 700, 
              color: '#111', 
              lineHeight: 1,
              cursor: orders.length > 0 ? 'pointer' : 'default'  
            }}
            onClick={orders.length > 0 ? toggleDropdown : undefined}
          >
            {count}
          </Typography>
        </Box>
        {orders.length > 0 && (
          <IconButton 
            onClick={toggleDropdown}
            sx={{ 
              ml: 1, 
              mt: 2.5,
              backgroundColor: isOpen ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.12)'
              }
            }}
            aria-label={isOpen ? "Hide orders" : "Show orders"}
          >
            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>
      
      {/* Order IDs dropdown */}
      {showDropdown && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            zIndex: 1000,
            mt: 1,
            p: 2,
            minWidth: '400px',
            maxHeight: `${maxHeight}px`,
            overflowY: 'auto',
            backgroundColor: 'white',
            borderRadius: '4px'
          }}
        >
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            {title} orders:
          </Typography>
          {orders.map((order) => (
            <Typography key={order.orderShortId} variant="body2" sx={{ mb: 0.5 }}>
              {order.orderShortId.slice(-5)} - {order.outletName}
            </Typography>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default OrdersIdsDropDown;